import { useGlobalErrStore } from '../../store/errorStore';
import { ErrorCode, convertStringToErrorCode } from '../../api/errorCode';
import { useLoadingStore } from '../../store/loadingStore';
import { ServiceType } from '../../enum/EnumRepository';
import { UpdatePdfAssetReq } from '../../dto/ReqDtoRepository';
import { base64ToFileWithMime, makePrefix } from '../../aws/awsHelper';
import { uploadFileWithPreSignedUrl } from '../../api/preSignedUrlApi';
import { usePreSigned } from './usePreSigned';
import { getConfig } from '../../env/envManager';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { FILE_MAX_SIZE } from '../../constants/limits';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';

interface UseFileResult {
  uploadPEPdf: (serviceType: ServiceType, data: UpdatePdfAssetReq, projectId?: string) => Promise<UpdatePdfAssetReq | null>;
}
const config = getConfig();


export const useFile = (): UseFileResult => {
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const { addLoading, popLoading } = useLoadingStore();
  const { setManagedErr, clearErr } = useGlobalErrStore();
  const { getPreSignedUrl } = usePreSigned();


  const uploadToS3 = async (file: File, aui: string, serviceType: ServiceType, identifier: string[]): Promise<string> => {
    const prefix = makePrefix(serviceType, identifier);
    const bucketName = config.s3BucketName;

    //   // Upload original file
    const originalFileKey = `uploads/${aui}/${prefix}/${file.name}`;

    // 여기서 pre-signed url 발급
    const preSignedUrl = await getPreSignedUrl({ fileName: originalFileKey, fileType: file.type, fileSize: file.size });

    // 여기서 pre-signed url로 파일 업로드
    if (preSignedUrl) {
      const uploaded = await runCurrentArchiveRequest(
        aui,
        () => uploadFileWithPreSignedUrl(preSignedUrl, file),
        () => true,
      );
      if (!uploaded) return '';
    }else{
      return '';
    }

    return `https://${bucketName}.s3.amazonaws.com/${originalFileKey}`;
  }

  //이미지 크기 확인
  const sizeCheckBig = (file: File): boolean => {
    if (file.size > FILE_MAX_SIZE) {
      return false;
    }
    return true;
  }

  //ProjectElement (PDF) 사용
  const uploadFileWithLocalUrlPE = async(
    serviceType: ServiceType,
    prevData: UpdatePdfAssetReq,
    projectId?: string
  ): Promise<UpdatePdfAssetReq | null> => {
    if (!pageOwner) return null;
    addLoading();
    clearErr();
    const { aui } = pageOwner;
    try {
      const localImageUrl = prevData.updateUploadFileReq.originUrl;
      const file = base64ToFileWithMime(localImageUrl);
      if (!sizeCheckBig(file)) {
        throw new Error(ErrorCode.BFE);
      }
      let originUrl: string;
      switch (serviceType) {
        case ServiceType.PDF:
          originUrl = await uploadToS3(file, aui, serviceType, [projectId!, prevData.id]);
          break;
        default:
          throw new Error('Unsupported service type');
      }
      if (originUrl === '') {
        return null;
      }
      return {
        ...prevData,
        updateUploadFileReq: {
          ...prevData.updateUploadFileReq,
          originUrl
        },
        fileSize: file.size
      };
    } catch (err) {
      const errCode = err instanceof Error ? err.message as ErrorCode : ErrorCode.AWS;
      setManagedErr({
        errCode: convertStringToErrorCode(errCode)
      });
      return null;
    } finally {
      popLoading();
    }
  }

  const uploadPEPdfHandler = (serviceType: ServiceType, data: UpdatePdfAssetReq, projectId?: string) => uploadFileWithLocalUrlPE(serviceType, data, projectId);

  return {
    uploadPEPdf: uploadPEPdfHandler
  };
}
